<template>
  <div class="expiry-management">
    <div class="lvyun-profile-banner">
      <h2>效期管理</h2>
      <p>管理商品效期，跟踪即将过期和已过期的商品，确保食品安全。</p>
    </div>

    <el-card class="stats-card" shadow="never">
      <el-row :gutter="20">
        <el-col :span="6">
          <div class="stat-item stat-total">
            <div class="stat-value">{{ stats.total }}</div>
            <div class="stat-label">总数量</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-item stat-normal">
            <div class="stat-value">{{ stats.normal }}</div>
            <div class="stat-label">正常</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-item stat-warning">
            <div class="stat-value">{{ stats.expiringSoon }}</div>
            <div class="stat-label">即将过期</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-item stat-danger">
            <div class="stat-value">{{ stats.expired }}</div>
            <div class="stat-label">已过期</div>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <el-card class="action-card" shadow="never">
      <template slot="header">
        <div class="card-header">
          <div class="card-title">商品效期列表</div>
          <div class="header-actions">
            <el-button type="primary" icon="el-icon-plus" class="btn-primary" @click="showAddDialog">
              入库登记
            </el-button>
            <div class="excel-actions">
              <el-upload
                class="el-upload"
                :show-file-list="false"
                :before-upload="handleImportExcel"
                accept=".xls,.xlsx"
              >
                <el-button type="default" size="small" class="btn-secondary">导入Excel</el-button>
              </el-upload>
              <el-button type="default" size="small" class="btn-secondary" @click="handleExport">
                导出Excel
              </el-button>
              <a class="template-link" href="javascript:;" @click.prevent="downloadTemplate">下载模板</a>
            </div>
          </div>
        </div>
      </template>

      <div class="filters-row">
        <el-input
          v-model="filters.keyword"
          placeholder="搜索商品名称、批号"
          clearable
          style="width: 240px;"
          prefix-icon="el-icon-search"
          @change="handleSearch"
        />
        <el-select v-model="filters.status" placeholder="状态筛选" clearable style="width: 140px; margin-left: 10px;" @change="handleSearch">
          <el-option label="全部" value="" />
          <el-option label="正常" value="正常" />
          <el-option label="即将过期" value="即将过期" />
          <el-option label="已过期" value="已过期" />
        </el-select>
        <el-button type="primary" icon="el-icon-search" class="btn-primary" style="margin-left: 10px;" @click="handleSearch">筛选</el-button>
        <el-button class="btn-secondary" icon="el-icon-refresh" style="margin-left: 10px;" @click="resetFilters">重置</el-button>
        <el-button v-if="selectedRows.length > 0" type="danger" icon="el-icon-delete" class="btn-secondary danger" style="margin-left: 10px;" @click="handleBatchDelete">
          批量删除 ({{ selectedRows.length }})
        </el-button>
      </div>

      <el-table
        ref="table"
        :data="tableData"
        style="width: 100%"
        stripe
        class="expiry-table"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55" />
        <el-table-column prop="product_name" label="商品名称" min-width="150" />
        <el-table-column prop="product_spec" label="规格" width="120" />
        <el-table-column prop="batch_number" label="生产批号" width="130" />
        <el-table-column label="入库时间" width="120">
          <template slot-scope="scope">
            {{ scope.row.inbound_time ? $formatDate(scope.row.inbound_time) : '—' }}
          </template>
        </el-table-column>
        <el-table-column label="到期时间" width="120">
          <template slot-scope="scope">
            {{ scope.row.expiry_date ? $formatDate(scope.row.expiry_date) : '—' }}
          </template>
        </el-table-column>
        <el-table-column label="剩余天数" width="100">
          <template slot-scope="scope">
            <span :class="getDaysClass(scope.row)">
              {{ getDaysText(scope.row) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="quantity" label="数量" width="80" />
        <el-table-column prop="unit" label="单位" width="70" />
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag size="mini" :type="getStatusType(scope.row.status)">
              {{ scope.row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="280" fixed="right">
          <template slot-scope="scope">
            <el-button size="small" class="btn-secondary" @click="editItem(scope.row)">编辑</el-button>
            <el-button size="small" class="btn-secondary" @click="printLabel(scope.row)">打印标签</el-button>
            <el-button size="small" class="btn-secondary" @click="sendReminder(scope.row)">提醒</el-button>
            <el-button size="small" class="btn-secondary danger" @click="deleteItem(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <admin-pagination
        :total="pagination.total"
        :page.sync="pagination.page"
        :page-size.sync="pagination.pageSize"
        @change="onPaginationChange"
      />
    </el-card>

    <el-dialog
      :title="dialogMode === 'create' ? '商品入库登记' : '编辑商品效期'"
      :visible.sync="dialogVisible"
      width="560px"
      destroy-on-close
      append-to-body
    >
      <el-form :model="form" :rules="rules" ref="form" label-width="100px">
        <el-form-item label="商品名称" prop="product_name">
          <el-input v-model="form.product_name" placeholder="请输入商品名称" />
        </el-form-item>
        <el-form-item label="商品规格" prop="product_spec">
          <el-input v-model="form.product_spec" placeholder="请输入商品规格（可选）" />
        </el-form-item>
        <el-form-item label="生产批号" prop="batch_number">
          <el-input v-model="form.batch_number" placeholder="请输入生产批号（可选）" />
        </el-form-item>
        <el-form-item label="入库时间" prop="inbound_time">
          <el-date-picker
            v-model="form.inbound_time"
            type="datetime"
            value-format="yyyy-MM-dd HH:mm:ss"
            placeholder="请选择入库时间"
            style="width: 100%;"
          />
        </el-form-item>
        <el-form-item label="到期时间" prop="expiry_date">
          <el-date-picker
            v-model="form.expiry_date"
            type="datetime"
            value-format="yyyy-MM-dd HH:mm:ss"
            placeholder="请选择到期时间"
            style="width: 100%;"
          />
        </el-form-item>
        <el-form-item label="数量" prop="quantity">
          <el-input-number v-model="form.quantity" :min="0" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="单位" prop="unit">
          <el-select v-model="form.unit" placeholder="请选择单位" style="width: 100%;">
            <el-option v-for="u in unitOptions" :key="u" :label="u" :value="u" />
          </el-select>
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input type="textarea" v-model="form.remark" placeholder="备注信息（可选）" :rows="2" />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button class="btn-secondary" @click="dialogVisible = false">取消</el-button>
        <el-button class="btn-primary" :loading="saving" @click="submitForm">确定</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="打印标签"
      :visible.sync="printDialogVisible"
      width="480px"
      destroy-on-close
      append-to-body
    >
      <el-form :model="printForm" label-width="90px">
        <el-form-item label="标签联数">
          <el-radio-group v-model="printForm.copies">
            <el-radio :label="1">一联标签</el-radio>
            <el-radio :label="2">二联标签</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-divider content-position="left">标签内容预览</el-divider>
        <div class="label-preview">
          <div class="label-row"><span class="label-key">商品名称：</span><span class="label-val">{{ printItem.product_name }}</span></div>
          <div class="label-row"><span class="label-key">生产批号：</span><span class="label-val">{{ printItem.batch_number || '-' }}</span></div>
          <div class="label-row"><span class="label-key">入库时间：</span><span class="label-val">{{ printItem.inbound_time ? $formatDateTime(printItem.inbound_time) : '—' }}</span></div>
          <div class="label-row"><span class="label-key">到期时间：</span><span class="label-val">{{ printItem.expiry_date ? $formatDate(printItem.expiry_date) : '—' }}</span></div>
        </div>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button class="btn-secondary" @click="printDialogVisible = false">取消</el-button>
        <el-button class="btn-primary" :loading="printing" @click="confirmPrint">确认打印</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  fetchExpiryItems,
  getExpiryItem,
  createExpiryItem,
  updateExpiryItem,
  deleteExpiryItem,
  batchDeleteExpiryItems,
  fetchExpiryStats,
  refreshExpiryStatuses,
  exportExpiryItems,
  downloadExpiryTemplate,
  importExpiryItems,
  sendTestReminder,
  printCatenLabel
} from '../api/expiry'
import { formatApiError } from '../utils/apiError'
import { formatDateTime, formatDate as formatBjDate, beijingNow } from '../utils/dateTimeUtils'
import { guardUploadFile, formatImportErrors, showImportResult } from '../utils/fileImport'

const UNIT_OPTIONS = ['件', '盒', '袋', '箱', '瓶', '罐', '个', '包', 'kg', 'g', 'L', 'mL']

export default {
  name: 'ExpiryManagement',
  data() {
    return {
      tableData: [],
      selectedRows: [],
      stats: {
        total: 0,
        normal: 0,
        expiringSoon: 0,
        expired: 0
      },
      filters: {
        keyword: '',
        status: ''
      },
      pagination: {
        page: 1,
        pageSize: 30,
        total: 0
      },
      dialogVisible: false,
      dialogMode: 'create',
      saving: false,
      currentId: null,
      form: {
        product_name: '',
        product_spec: '',
        batch_number: '',
        inbound_time: '',
        expiry_date: '',
        quantity: 0,
        unit: '件',
        remark: ''
      },
      rules: {
        product_name: [{ required: true, message: '请输入商品名称', trigger: 'blur' }],
        expiry_date: [{ required: true, message: '请选择到期时间', trigger: 'change' }],
        quantity: [{ required: true, message: '请输入数量', trigger: 'blur' }]
      },
      unitOptions: UNIT_OPTIONS,
      printDialogVisible: false,
      printItem: {},
      printForm: {
        copies: 1
      },
      printing: false
    }
  },
  mounted() {
    this.loadData()
    this.loadStats()
  },
  methods: {
    async loadData() {
      try {
        const data = await fetchExpiryItems({
          keyword: this.filters.keyword,
          status: this.filters.status,
          page: this.pagination.page,
          pageSize: this.pagination.pageSize
        })
        if (data.code === 200) {
          this.tableData = data.list || []
          this.pagination.total = data.total || 0
        }
      } catch (e) {
        this.$message.error(formatApiError(e))
      }
    },
    async loadStats() {
      try {
        const data = await fetchExpiryStats()
        if (data.code === 200) {
          this.stats = data.data || {}
        }
      } catch (e) {
        console.error('Failed to load stats:', e)
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.loadData()
    },
    resetFilters() {
      this.filters.keyword = ''
      this.filters.status = ''
      this.pagination.page = 1
      this.loadData()
    },
    handlePageChange(page) {
      this.pagination.page = page
      this.loadData()
    },
    onPaginationChange({ page, pageSize }) {
      this.pagination.page = page
      this.pagination.pageSize = pageSize
      this.loadData()
    },
    handleSelectionChange(val) {
      this.selectedRows = val
    },
    showAddDialog() {
      this.dialogMode = 'create'
      this.currentId = null
      this.form = {
        product_name: '',
        product_spec: '',
        batch_number: '',
        inbound_time: this.getCurrentDatetime(),
        expiry_date: '',
        quantity: 0,
        unit: '件',
        remark: ''
      }
      this.dialogVisible = true
    },
    editItem(row) {
      this.dialogMode = 'edit'
      this.currentId = row.id
      this.form = {
        product_name: row.product_name || '',
        product_spec: row.product_spec || '',
        batch_number: row.batch_number || '',
        inbound_time: row.inbound_time ? this.formatDatetime(row.inbound_time) : '',
        expiry_date: row.expiry_date ? this.formatDatetime(row.expiry_date) : '',
        quantity: row.quantity || 0,
        unit: row.unit || '件',
        remark: row.remark || ''
      }
      this.dialogVisible = true
    },
    async submitForm() {
      try {
        await this.$refs.form.validate()
      } catch {
        return
      }
      this.saving = true
      try {
        if (this.dialogMode === 'create') {
          await createExpiryItem(this.form)
          this.$message.success('添加成功')
        } else {
          await updateExpiryItem(this.currentId, this.form)
          this.$message.success('更新成功')
        }
        this.dialogVisible = false
        this.loadData()
        this.loadStats()
      } catch (e) {
        this.$message.error(formatApiError(e))
      } finally {
        this.saving = false
      }
    },
    async deleteItem(row) {
      try {
        await this.$confirm('确定要删除这条记录吗？', '提示', { type: 'warning' })
        await deleteExpiryItem(row.id)
        this.$message.success('删除成功')
        this.loadData()
        this.loadStats()
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error(formatApiError(e))
        }
      }
    },
    async handleBatchDelete() {
      if (this.selectedRows.length === 0) return
      try {
        await this.$confirm(`确定要删除选中的 ${this.selectedRows.length} 条记录吗？`, '提示', { type: 'warning' })
        const ids = this.selectedRows.map(r => r.id)
        await batchDeleteExpiryItems(ids)
        this.$message.success('批量删除成功')
        this.selectedRows = []
        this.loadData()
        this.loadStats()
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error(formatApiError(e))
        }
      }
    },
    printLabel(row) {
      this.printItem = { ...row }
      this.printForm.copies = 1
      this.printDialogVisible = true
    },
    async confirmPrint() {
      this.printing = true
      try {
        const labelContent = this.buildLabelContent(this.printItem)
        const copies = this.printForm.copies
        for (let i = 0; i < copies; i++) {
          await this.printToCaten(labelContent)
        }
        this.$message.success(`已发送 ${copies} 联标签到打印机`)
        this.printDialogVisible = false
      } catch (e) {
        this.$message.error('打印失败：' + formatApiError(e))
      } finally {
        this.printing = false
      }
    },
    buildLabelContent(item) {
      const lines = [
        '{NAME:' + (item.product_name || '') + '}',
        '{BATCH:' + (item.batch_number || '-') + '}',
        '{IN:' + (item.inbound_time ? this.formatDate(item.inbound_time) : '-') + '}',
        '{EXP:' + (item.expiry_date ? this.formatDate(item.expiry_date) : '-') + '}',
        '{COPY:' + this.printForm.copies + '}'
      ]
      return lines.join('')
    },
    async printToCaten(content) {
      await printCatenLabel(content)
    },
    async sendReminder(row) {
      try {
        await this.$confirm('确定发送测试提醒到飞书群？', '提示', { type: 'info' })
        await sendTestReminder(row.id)
        this.$message.success('提醒已发送')
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error(formatApiError(e))
        }
      }
    },
    async handleImportExcel(file) {
      if (!guardUploadFile(this, file)) return false
      const formData = new FormData()
      formData.append('file', file)
      try {
        const data = await importExpiryItems(formData)
        if (data.code === 200) {
          showImportResult(this, data, { successMessage: '导入完成' })
          this.loadData()
          this.loadStats()
        } else {
          this.$message.error(data.message || '导入失败')
        }
      } catch (e) {
        const respData = e && e.response && e.response.data
        const msg = (respData && respData.message) || formatApiError(e)
        const errText = respData && respData.data ? formatImportErrors(respData.data) : ''
        if (errText) {
          this.$alert(`${msg}\n\n${errText}`, '导入校验失败', { type: 'error' })
        } else {
          this.$message.error('导入失败：' + msg)
        }
      }
      return false
    },
    async handleExport() {
      try {
        await exportExpiryItems({
          keyword: this.filters.keyword,
          status: this.filters.status
        })
        this.$message.success('导出成功')
      } catch (e) {
        this.$message.error('导出失败：' + formatApiError(e))
      }
    },
    async downloadTemplate() {
      try {
        await downloadExpiryTemplate()
      } catch (e) {
        this.$message.error('模板下载失败')
      }
    },
    formatDate(d) {
      const s = formatBjDate(d)
      return s === '—' ? '' : s
    },
    formatDatetime(d) {
      const s = formatDateTime(d)
      return s === '—' ? '' : s
    },
    getCurrentDatetime() {
      return beijingNow()
    },
    getDaysClass(row) {
      if (!row.days_remaining && row.days_remaining !== 0) return ''
      if (row.days_remaining < 0) return 'days-expired'
      if (row.days_remaining <= 7) return 'days-warning'
      return 'days-normal'
    },
    getDaysText(row) {
      if (!row.days_remaining && row.days_remaining !== 0) return '-'
      if (row.days_remaining < 0) return `已过期${Math.abs(row.days_remaining)}天`
      if (row.days_remaining === 0) return '今日到期'
      return `${row.days_remaining}天`
    },
    getStatusType(status) {
      if (status === '已过期') return 'danger'
      if (status === '即将过期') return 'warning'
      return 'success'
    }
  }
}
</script>

<style scoped>
.expiry-management {
  padding: 20px;
}

.lvyun-profile-banner {
  margin-bottom: 20px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  color: white;
}

.lvyun-profile-banner h2 {
  margin: 0 0 8px 0;
  font-size: 22px;
  font-weight: 600;
}

.lvyun-profile-banner p {
  margin: 0;
  font-size: 14px;
  opacity: 0.9;
}

.stats-card {
  margin-bottom: 20px;
}

.stat-item {
  text-align: center;
  padding: 16px;
  border-radius: 8px;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
}

.stat-label {
  font-size: 14px;
  color: #666;
  margin-top: 4px;
}

.stat-total {
  background: #f5f7fa;
}

.stat-normal {
  background: #e8f8f0;
}

.stat-normal .stat-value {
  color: #2ecc71;
}

.stat-warning {
  background: #fef9e7;
}

.stat-warning .stat-value {
  color: #f39c12;
}

.stat-danger {
  background: #fdedec;
}

.stat-danger .stat-value {
  color: #e74c3c;
}

.action-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.excel-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 10px;
}

.template-link {
  color: #409eff;
  font-size: 13px;
  text-decoration: none;
}

.template-link:hover {
  text-decoration: underline;
}

.filters-row {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.expiry-table {
  margin-top: 10px;
}

.days-normal {
  color: #2ecc71;
  font-weight: 600;
}

.days-warning {
  color: #f39c12;
  font-weight: 600;
}

.days-expired {
  color: #e74c3c;
  font-weight: 600;
}

.label-preview {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
  font-size: 14px;
}

.label-row {
  display: flex;
  margin-bottom: 8px;
}

.label-row:last-child {
  margin-bottom: 0;
}

.label-key {
  font-weight: 600;
  min-width: 90px;
  color: #333;
}

.label-val {
  color: #666;
}
</style>